import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:openai-error-mapper');

/**
 * OpenAI Error Mapper
 *
 * Maps OpenAI error codes to user-friendly messages with actionable guidance.
 * Based on OpenAI API error codes: https://platform.openai.com/docs/guides/error-codes
 */

export type OpenAIErrorCode =
  | 'context_length_exceeded'
  | 'rate_limit_exceeded'
  | 'insufficient_quota'
  | 'invalid_request_error'
  | 'authentication_error'
  | 'permission_denied'
  | 'not_found_error'
  | 'conflict_error'
  | 'internal_error'
  | 'service_unavailable'
  | 'file_too_large'
  | 'content_too_large';

export type ErrorAction =
  | 'new-chat'
  | 'remove-attachments'
  | 'shorten-message'
  | 'retry'
  | 'contact-support'
  | 'upgrade-plan'
  | 'reduce-file-size';

export type MappedError = {
  title: string;
  message: string;
  actions: Array<{
    id: ErrorAction;
    label: string;
    variant?: 'default' | 'destructive' | 'outline';
  }>;
  severity: 'error' | 'warning' | 'info';
};

/**
 * Extract OpenAI error code from error message
 * Backend formats errors as: "[error_code] message"
 */
export const extractErrorCode = (errorMessage: string): string | null => {
  const match = errorMessage.match(/\[([^\]]+)\]/);
  return match ? (match[1] ?? null) : null;
};

/**
 * Check if error message indicates context window overflow
 */
export const isContextLengthError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('context') ||
    lowerMessage.includes('context window') ||
    lowerMessage.includes('context_length') ||
    lowerMessage.includes('too long') ||
    lowerMessage.includes('token limit') ||
    lowerMessage.includes('maximum context') ||
    lowerMessage.includes('exceeds') ||
    extractErrorCode(errorMessage) === 'context_length_exceeded'
  );
};

/**
 * Check if error message indicates rate limiting
 */
export const isRateLimitError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('rate limit') ||
    lowerMessage.includes('too many requests') ||
    lowerMessage.includes('429') ||
    extractErrorCode(errorMessage) === 'rate_limit_exceeded'
  );
};

/**
 * Check if error message indicates quota/billing issues
 */
export const isQuotaError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('quota') ||
    lowerMessage.includes('insufficient_quota') ||
    lowerMessage.includes('billing') ||
    lowerMessage.includes('payment') ||
    extractErrorCode(errorMessage) === 'insufficient_quota'
  );
};

/**
 * Check if error message indicates network/connection issues
 */
export const isNetworkError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('network') ||
    lowerMessage.includes('connection') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('econnrefused') ||
    lowerMessage.includes('err_connection_refused') ||
    lowerMessage.includes('network unreachable') ||
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('etimedout') ||
    lowerMessage === 'network error' // Exact match for generic network error
  );
};

/**
 * Check if error message indicates file size limit exceeded
 */
export const isFileTooLargeError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('file size') ||
    lowerMessage.includes('file too large') ||
    lowerMessage.includes('exceeds the maximum limit') ||
    lowerMessage.includes('exceeds maximum') ||
    extractErrorCode(errorMessage) === 'file_too_large'
  );
};

/**
 * Check if error message indicates extracted content is too large
 */
export const isContentTooLargeError = (errorMessage: string): boolean => {
  const lowerMessage = errorMessage.toLowerCase();
  return (
    lowerMessage.includes('content is too large') ||
    lowerMessage.includes('content too large') ||
    lowerMessage.includes('tokens estimated') ||
    (lowerMessage.includes('too large for ai processing') && lowerMessage.includes('tokens')) ||
    extractErrorCode(errorMessage) === 'content_too_large'
  );
};

/**
 * Map OpenAI error to user-friendly message with actions
 */
export const mapOpenAIError = (errorMessage: string | unknown): MappedError => {
  // Ensure errorMessage is a string
  let errorString: string;
  if (typeof errorMessage === 'string') {
    errorString = errorMessage;
  } else if (errorMessage instanceof Error) {
    errorString = errorMessage.message;
  } else if (errorMessage && typeof errorMessage === 'object') {
    // Try to extract error message from object
    const errorObj = errorMessage as Record<string, unknown>;
    if ('message' in errorObj && typeof errorObj.message === 'string') {
      errorString = errorObj.message;
    } else if ('error' in errorObj && typeof errorObj.error === 'string') {
      errorString = errorObj.error;
    } else {
      errorString = JSON.stringify(errorMessage);
    }
  } else {
    errorString = String(errorMessage);
  }

  // Log error for debugging
  logger.info('🔍 [ERROR-MAPPER] Mapping error:', {
    message: errorString,
    errorCode: extractErrorCode(errorString),
  });

  // File too large (before AI processing)
  if (isFileTooLargeError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: File Too Large Error');
    return {
      title: 'File Size Limit Exceeded',
      message:
        'Your file is too large to process. ' +
        "This is a limit set by OpenAI's AI service, not our application. " +
        'To continue, please reduce the file size or split it into smaller files.',
      actions: [
        {
          id: 'reduce-file-size',
          label: 'How to Reduce File Size',
          variant: 'default',
        },
        {
          id: 'remove-attachments',
          label: 'Remove Attachment',
          variant: 'outline',
        },
      ],
      severity: 'warning',
    };
  }

  // Content too large (after extraction - too many tokens)
  if (isContentTooLargeError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: Content Too Large Error');
    return {
      title: 'Document Content Too Large',
      message:
        "Your document contains too much text for OpenAI's AI to process in one request. " +
        "This is a limit of OpenAI's service (approximately 100,000 tokens per request). " +
        'Please split your document into smaller sections or reduce the amount of data.',
      actions: [
        {
          id: 'reduce-file-size',
          label: 'Reduce Document Size',
          variant: 'default',
        },
        {
          id: 'remove-attachments',
          label: 'Remove Attachment',
          variant: 'outline',
        },
        {
          id: 'new-chat',
          label: 'Start New Chat',
          variant: 'outline',
        },
      ],
      severity: 'warning',
    };
  }

  // Context length exceeded
  if (isContextLengthError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: Context Length Error');
    return {
      title: 'Conversation Too Long',
      message:
        "This chat has grown too long for OpenAI's AI to process. " +
        "This is a limitation of OpenAI's service (approximately 128,000 tokens per conversation). " +
        '\n\n' +
        'Starting a new chat will give you a fresh conversation with full AI capabilities.',
      actions: [
        {
          id: 'new-chat',
          label: 'Start Fresh Chat',
          variant: 'default',
        },
        {
          id: 'remove-attachments',
          label: 'Remove Attachments',
          variant: 'outline',
        },
        {
          id: 'shorten-message',
          label: 'Edit Message',
          variant: 'outline',
        },
      ],
      severity: 'warning',
    };
  }

  // Rate limit exceeded
  if (isRateLimitError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: Rate Limit Error');
    return {
      title: 'Too Many Requests',
      message:
        "You've sent too many messages in a short time. " +
        'Please wait a moment before trying again.',
      actions: [
        {
          id: 'retry',
          label: 'Retry',
          variant: 'default',
        },
      ],
      severity: 'warning',
    };
  }

  // Quota/billing issues
  if (isQuotaError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: Quota/Billing Error');
    return {
      title: 'Usage Limit Reached',
      message:
        'Your organization has reached its usage limit. ' +
        'Please contact your administrator or upgrade your plan.',
      actions: [
        {
          id: 'contact-support',
          label: 'Contact Support',
          variant: 'default',
        },
        {
          id: 'upgrade-plan',
          label: 'View Plans',
          variant: 'outline',
        },
      ],
      severity: 'error',
    };
  }

  // Network/Connection errors
  if (isNetworkError(errorString)) {
    logger.info('✅ [ERROR-MAPPER] Detected: Network/Connection Error');
    return {
      title: 'Connection Error',
      message:
        'Unable to reach the AI service. Please check your internet connection and try again. ' +
        'If the problem persists, the service may be temporarily unavailable.',
      actions: [
        {
          id: 'retry',
          label: 'Try Again',
          variant: 'default',
        },
        {
          id: 'contact-support',
          label: 'Report Issue',
          variant: 'outline',
        },
      ],
      severity: 'error',
    };
  }

  // Server errors (5xx)
  if (
    errorString.includes('500') ||
    errorString.includes('503') ||
    errorString.includes('internal')
  ) {
    logger.info('✅ [ERROR-MAPPER] Detected: Server Error (5xx)');
    return {
      title: 'Service Error',
      message:
        'The AI service is experiencing issues. This is usually temporary. ' +
        'Please try again in a few moments.',
      actions: [
        {
          id: 'retry',
          label: 'Try Again',
          variant: 'default',
        },
        {
          id: 'contact-support',
          label: 'Report Issue',
          variant: 'outline',
        },
      ],
      severity: 'error',
    };
  }

  // Generic fallback with OpenAI attribution
  logger.warn('⚠️ [ERROR-MAPPER] Using generic fallback - no specific error type matched');
  logger.warn('⚠️ [ERROR-MAPPER] Consider adding a handler for this error type:', errorString);

  // Check if error message contains OpenAI-specific keywords
  const isLikelyOpenAIError =
    errorString.toLowerCase().includes('openai') ||
    errorString.toLowerCase().includes('model') ||
    errorString.toLowerCase().includes('token') ||
    errorString.toLowerCase().includes('api');

  const title = isLikelyOpenAIError ? 'AI Service Error' : 'Chat Error';
  const message = isLikelyOpenAIError
    ? `The AI service (OpenAI) encountered an issue: ${errorString}\n\nThis is not a problem with our application. Please try again or contact support if the issue persists.`
    : errorString || 'An unexpected error occurred. Please try again.';

  return {
    title,
    message,
    actions: [
      {
        id: 'retry',
        label: 'Try Again',
        variant: 'default',
      },
      {
        id: 'new-chat',
        label: 'Start New Chat',
        variant: 'outline',
      },
      {
        id: 'contact-support',
        label: 'Contact Support',
        variant: 'outline',
      },
    ],
    severity: 'error',
  };
};

/**
 * Format error for logging/debugging
 */
export const formatErrorForLogging = (error: unknown): Record<string, unknown> => {
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };
  }

  if (error && typeof error === 'object') {
    return {
      ...error,
      errorString: String(error),
    };
  }

  return {
    error: String(error),
  };
};
